import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  Box,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { CVPreviewSidebar } from "../components/common/CVPreviewSidebar";
import { ROUTES } from "../constants/routes";
import { EditModeProvider } from "../context/EditModeContext";
import { CVDownloadMenu } from "../features/CVEditor/components/CVDownloadMenu";
import {
  Certifications,
  PersonCertificationService,
} from "../features/CVEditor/features/Certification";
import { Education } from "../features/CVEditor/features/Education";
import { Languages } from "../features/CVEditor/features/Languages";
import { ProjectExperience } from "../features/CVEditor/features/ProjectExperience";
import { QualificationSummaries } from "../features/CVEditor/features/QualificationSummaries";
import { SkillsOverview } from "../features/CVEditor/features/SkillsOverview";
import { WorkExperienceService } from "../features/CVEditor/features/WorkExperience";
import { WorkExperience } from "../features/CVEditor/features/WorkExperience/components/WorkExperience";
import { QualificationSummaryService } from "../features/QualificationSummary/services/qualificationSummaryService";
import { useCurrentUser } from "../hooks/queries/useCurrentUser";
import { Person } from "../models/person.model";
import { CoursesService } from "../services/coursesService";
import { EducationService } from "../services/educationService";
import { LanguageSkillService } from "../services/languageSkillService";
import { PersonService } from "../services/personService";
import { PersonTechnologyService } from "../services/personTechnologyService";
import { ProjectService } from "../services/projectService";
import { TechnologyService } from "../services/technologyService";
import { UserCV } from "../types/cv";

function CVDetailPageContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole, isAuthenticated, updateUserRole } = useAuth();
  const [roleLoading, setRoleLoading] = useState(false);
  const { data: reactQueryUser, isLoading: userLoading } = useCurrentUser();
  const roleLoadingRef = useRef(false);

  const [cv, setCV] = useState<UserCV | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Loading states for individual sections
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [educationLoading, setEducationLoading] = useState(true);
  const [workExperienceLoading, setWorkExperienceLoading] = useState(true);
  const [certificationsLoading, setCertificationsLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [languagesLoading, setLanguagesLoading] = useState(true);

  // Service instances
  const personService = new PersonService();
  const technologyService = new TechnologyService();
  const workExperienceService = new WorkExperienceService();
  const educationService = new EducationService();
  const personCertificationService = new PersonCertificationService();
  const coursesService = new CoursesService();
  const languageSkillService = new LanguageSkillService();
  const projectService = new ProjectService();
  const qualificationSummaryService = new QualificationSummaryService();
  const personTechnologyService = new PersonTechnologyService();

  // Make sure user role is loaded
  useEffect(() => {
    if (
      isAuthenticated &&
      !userLoading &&
      !userRole &&
      reactQueryUser &&
      !roleLoadingRef.current
    ) {
      roleLoadingRef.current = true;
      console.log("User role data loaded for CV detail page:", reactQueryUser);
      updateUserRole(reactQueryUser);
      roleLoadingRef.current = false;
    }
  }, [isAuthenticated, userRole, reactQueryUser, userLoading]);

  useEffect(() => {
    if (id) {
      loadCVData(parseInt(id));
    }
  }, [id]);

  const loadCVData = async (personId: number) => {
    setIsLoading(true);
    // Set all section loading states to true
    setSummaryLoading(true);
    setEducationLoading(true);
    setWorkExperienceLoading(true);
    setCertificationsLoading(true);
    setProjectsLoading(true);
    setLanguagesLoading(true);

    try {
      // Step 1: Load person data first (highest priority)
      const personData = await personService.getPersonById(personId);
      setPerson(personData);

      // Create a minimal CV object with basic user info to show while other data loads
      const fullName = `${personData.firstName} ${personData.lastName}`;
      const minimalCV: UserCV = {
        id: personData.id.toString(),
        personId: personData.id.toString(),
        name: fullName,
        title: personData.positions?.[0] || "No position",
        email: personData.email,
        phone: "",
        profilePhoto: personData.imagePath,
        externalRoles: [],
        projectExperience: [],
        qualificationSummaries: [],
        languages: [],
        honors: [],
        publications: [],
        positions: [],
        presentations: [],
        courses: [],
        certifications: [],
        workExperience: [],
        education: [],
        skills: [],
      };

      // Set minimal CV first so UI can render basic profile
      setCV(minimalCV);
      setIsLoading(false);

      // Step 2: Load high priority data first (summaries and technologies for sidebar)
      try {
        const [qualificationSummariesData, technologiesData] =
          await Promise.all([
            qualificationSummaryService.getSummariesByPersonId(personId).catch(() => []),
            personTechnologyService
              .getPersonFavoriteTechnologies(personId)
              .catch(() => []),
          ]);

        // Update CV with summaries and skills
        setCV((prevCV) => {
          if (!prevCV) return prevCV;

          const updatedCV = { ...prevCV };

          // Update summaries
          updatedCV.qualificationSummaries = qualificationSummariesData.map(
            (summary: any) => ({
              id: summary.id,
              personId: summary.personId,
              name: summary.name || "",
              description: summary.description || "",
              selectedForCv: summary.selectedForCv || false,
            })
          );

          // Update title from summaries if available
          updatedCV.title = qualificationSummariesData[0]?.name || prevCV.title;

          // Update skills
          updatedCV.skills = technologiesData.map(
            (tech: any, index: number) => ({
              id: `tech-${index}-${Date.now()}`,
              name: tech.technology?.name || "",
              yearsOfExperience: 0,
              category: "Technology competencies",
              includeInCV: true,
            })
          );

          return updatedCV;
        });

        setSummaryLoading(false);
      } catch (error) {
        console.error("Error loading summary data:", error);
        setSummaryLoading(false);
      }

      // Step 3: Load language data (medium priority)
      try {
        const languagesData = await languageSkillService
          .getDataByPersonID(personId)
          .catch(() => []);

        setCV((prevCV) => {
          if (!prevCV) return prevCV;

          return {
            ...prevCV,
            languages: languagesData.map((lang: any) => ({
              id: lang.id.toString(),
              languageId: lang.language?.id || 0,
              name: lang.language?.englishName || "Unknown",
              proficiencyId: lang.proficiency?.id || 0,
              proficiency: lang.proficiency?.level || "Unknown",
              includeInCV: true,
            })),
          };
        });

        setLanguagesLoading(false);
      } catch (error) {
        console.error("Error loading language data:", error);
        setLanguagesLoading(false);
      }

      // Step 4: Load remaining data in parallel
      Promise.all([
        // Work experience
        workExperienceService
          .getWorkItemsByUserID(personId)
          .then((workExperienceData) => {
            setCV((prevCV) => {
              if (!prevCV) return prevCV;

              return {
                ...prevCV,
                workExperience: workExperienceData.map((we: any) => {
                  // Use UTC methods to extract date parts to avoid timezone issues
                  let fromDateStr = "";
                  if (we.dateStart) {
                    const fromDate = new Date(we.dateStart);
                    fromDateStr = `${fromDate.getUTCFullYear()}-${String(fromDate.getUTCMonth() + 1).padStart(2, '0')}-${String(fromDate.getUTCDate()).padStart(2, '0')}`;
                  }

                  let toDateStr = "";
                  if (we.dateEnd) {
                    const toDate = new Date(we.dateEnd);
                    toDateStr = `${toDate.getUTCFullYear()}-${String(toDate.getUTCMonth() + 1).padStart(2, '0')}-${String(toDate.getUTCDate()).padStart(2, '0')}`;
                  }

                  return {
                    id: we.id.toString(),
                    fromDate: fromDateStr,
                    toDate: toDateStr,
                    employer: we.employer || "",
                    position: we.position || "",
                    description: we.description || "",
                    technologies: (we.technologies || [])
                      .map((tech: any) => ({
                        name: tech.technology?.name || tech.name || tech,
                        isFavorite: tech.favourite || false,
                      }))
                      .filter((t: { name: string; isFavorite: boolean }) => Boolean(t.name)),
                  };
                }),
              };
            });
            setWorkExperienceLoading(false);
          })
          .catch(() => {
            setWorkExperienceLoading(false);
            return [];
          }),

        // Education
        educationService
          .getDataByPersonID(personId)
          .then((educationData) => {
            setCV((prevCV) => {
              if (!prevCV) return prevCV;

              return {
                ...prevCV,
                education: educationData.map((edu: any) => ({
                  id: edu.id.toString(),
                  fromDate: edu.dateStart
                    ? new Date(edu.dateStart)
                      .toISOString()
                      .slice(0, 7)
                      .replace("-", "/")
                    : "",
                  toDate: edu.dateEnd
                    ? new Date(edu.dateEnd)
                      .toISOString()
                      .slice(0, 7)
                      .replace("-", "/")
                    : "",
                  school: edu.institution?.name || "",
                  degree: edu.degree?.name || "",
                  description: "",
                })),
              };
            });
            setEducationLoading(false);
          })
          .catch(() => {
            setEducationLoading(false);
            return [];
          }),

        // Certifications
        personCertificationService
          .getDataByPersonID(personId)
          .then((certificationsData) => {
            setCV((prevCV) => {
              if (!prevCV) return prevCV;

              return {
                ...prevCV,
                certifications: certificationsData.map((cert: any) => ({
                  id: cert.id.toString(),
                  startDate:
                    cert.date?.toISOString().slice(0, 7).replace("-", "/") ||
                    "",
                  expiryDate: "",
                  name: cert.name || "",
                  isStarred: cert.isStarred ?? false,
                })),
              };
            });
            setCertificationsLoading(false);
          })
          .catch(() => {
            setCertificationsLoading(false);
            return [];
          }),

        // Project experience
        projectService
          .getProjectByPersonID(personId)
          .then((projectsData) => {
            setCV((prevCV) => {
              if (!prevCV) return prevCV;

              return {
                ...prevCV,
                projectExperience: projectsData.map((project: any) => ({
                  id: project.id.toString(),
                  customerId: project.customerId || 0,
                  customerName: project.customerName || "",
                  internalProjectId: project.internalProjectId || 0,
                  personId: project.personId || personId,
                  technologies: project.technologies || [],
                  info: {
                    description:
                      project.description || project.info?.description || "",
                    name: project.name || project.info?.name || "",
                  },
                  endDate: project.endDate
                    ? project.endDate instanceof Date
                      ? project.endDate.toISOString()
                      : String(project.endDate)
                    : "",
                  startDate: project.startDate
                    ? project.startDate instanceof Date
                      ? project.startDate.toISOString()
                      : String(project.startDate)
                    : "",
                  roles: (project.roles || []).map((r: any) => ({
                    name: r.name?.toString() || "",
                  })),
                  responsibilities: project.responsibilities || "",
                  allocationPercent: project.allocationPercent || 100,
                })),
              };
            });
            setProjectsLoading(false);
          })
          .catch(() => {
            setProjectsLoading(false);
            return [];
          }),

        // Courses
        coursesService
          .getDataByPersonID(personId)
          .then((coursesData) => {
            setCV((prevCV) => {
              if (!prevCV) return prevCV;

              return {
                ...prevCV,
                courses: coursesData.map((course: any) => ({
                  id: course.id.toString(),
                  name: course.title || "",
                  yearCompleted: course.date?.getFullYear().toString() || "",
                  includeInCV: course.isStarred ?? false,
                })),
              };
            });
          })
          .catch(() => []),
      ]);
    } catch (error) {
      console.error("Error loading person data:", error);
      notifications.show({
        title: "Error",
        message: "Failed to load CV data",
        color: "red",
      });
      setIsLoading(false);

      // Set all section loading states to false in case of error
      setSummaryLoading(false);
      setEducationLoading(false);
      setWorkExperienceLoading(false);
      setCertificationsLoading(false);
      setProjectsLoading(false);
      setLanguagesLoading(false);
    }
  };

  // These are no-op functions since this page is strictly read-only
  const handleUpdateQualificationSummaries = () => {
    notifications.show({
      title: "Read-only mode",
      message:
        "This page is read-only. Please use the Edit CV button to make changes.",
      color: "blue",
    });
  };
  const handleUpdateEducation = () => {
    notifications.show({
      title: "Read-only mode",
      message:
        "This page is read-only. Please use the Edit CV button to make changes.",
      color: "blue",
    });
  };
  const handleUpdateWorkExperience = () => {
    notifications.show({
      title: "Read-only mode",
      message:
        "This page is read-only. Please use the Edit CV button to make changes.",
      color: "blue",
    });
  };
  const handleUpdateCertifications = () => {
    notifications.show({
      title: "Read-only mode",
      message:
        "This page is read-only. Please use the Edit CV button to make changes.",
      color: "blue",
    });
  };
  const handleUpdateProjectExperience = () => {
    notifications.show({
      title: "Read-only mode",
      message:
        "This page is read-only. Please use the Edit CV button to make changes.",
      color: "blue",
    });
  };

  // Adapter function to match the CVPreviewSidebar's expected function signature
  const handleCVPreviewDownload = (format?: string) => {
    if (
      format === "pdf" ||
      format === "template" ||
      format === "without-names"
    ) {
      // Use the format directly with the sidebar's download functionality
      return format;
    }
    // Default to PDF if no format is specified
    return "pdf";
  };

  const goBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="lg" py={50}>
          <Loader size="lg" />
          <Text size="md" c="dimmed">
            Loading CV data...
          </Text>
        </Stack>
      </Container>
    );
  }

  if (!cv || !person) {
    return (
      <Container size="xl" py="xl">
        <Paper p="xl" withBorder>
          <Stack align="center" gap="md">
            <Text size="lg" c="red">
              CV not found
            </Text>
            <Button
              leftSection={<ArrowLeftIcon width={16} height={16} />}
              onClick={goBack}
            >
              Go Back
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  const handleCloseSidebar = () => {
    // In this app, we'll keep the sidebar always visible
  };

  return (
    <Container size="xl" py="xl" pos="relative">
      <Group justify="space-between" mb="xl">
        <Group gap="md">
          <Button
            variant="subtle"
            leftSection={<ArrowLeftIcon width={16} height={16} />}
            onClick={goBack}
          >
            Back to list
          </Button>
          <Title order={2}>{cv.name}'s CV</Title>
        </Group>

        <Group gap="md">
          <CVDownloadMenu
            personId={parseInt(cv.personId)}
            userName={cv.name}
            buttonProps={{
              variant: "light",
              size: "md",
            }}
          />
          <Button
            variant="filled"
            color="brand"
            size="md"
            onClick={() => navigate(ROUTES.PERSON_SKILLS(cv.personId))}
          >
            View Skills
          </Button>
          <Button
            size="md"
            onClick={() => navigate(ROUTES.CV_EDITOR_WITH_ID(cv.personId))}
          >
            Edit CV
          </Button>
        </Group>
      </Group>

      <Group align="flex-start" gap="xl">
        <div style={{ flex: 1 }}>
          <Tabs defaultValue="summary">
            <Tabs.List>
              <Tabs.Tab value="summary">Summary</Tabs.Tab>
              <Tabs.Tab value="education">Education</Tabs.Tab>
              <Tabs.Tab value="experience">Work Experience</Tabs.Tab>
              <Tabs.Tab value="certifications">Certifications</Tabs.Tab>
              <Tabs.Tab value="projects">Project Experience</Tabs.Tab>
              <Tabs.Tab value="languages">Languages</Tabs.Tab>
              <Tabs.Tab value="skills">Skills</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="summary" pt="md">
              {summaryLoading ? (
                <Box p="md">
                  <Loader size="sm" />
                </Box>
              ) : (
                <QualificationSummaries
                  summaries={cv.qualificationSummaries}
                  onChange={handleUpdateQualificationSummaries}
                  personId={id ? parseInt(id) : undefined}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="education" pt="md">
              {educationLoading ? (
                <Box p="md">
                  <Loader size="sm" />
                </Box>
              ) : (
                <Education
                  education={cv.education}
                  onChange={handleUpdateEducation}
                  personId={id ? parseInt(id) : undefined}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="experience" pt="md">
              {workExperienceLoading ? (
                <Box p="md">
                  <Loader size="sm" />
                </Box>
              ) : (
                <WorkExperience
                  workExperiences={cv.workExperience}
                  onChange={handleUpdateWorkExperience}
                  userId={id ? parseInt(id) : undefined}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="certifications" pt="md">
              {certificationsLoading ? (
                <Box p="md">
                  <Loader size="sm" />
                </Box>
              ) : (
                <Certifications
                  certifications={cv.certifications}
                  onChange={handleUpdateCertifications}
                  personId={id ? parseInt(id) : undefined}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="projects" pt="md">
              {projectsLoading ? (
                <Box p="md">
                  <Loader size="sm" />
                </Box>
              ) : (
                <ProjectExperience
                  projects={cv.projectExperience}
                  onChange={handleUpdateProjectExperience}
                  userId={id ? parseInt(id) : undefined}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="languages" pt="md">
              {languagesLoading ? (
                <Box p="md">
                  <Loader size="sm" />
                </Box>
              ) : (
                <Languages userId={parseInt(cv.personId)} />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="skills" pt="md">
              <SkillsOverview
                projectExperience={cv.projectExperience}
                skills={cv.skills}
                onChange={() => { }} // Read-only mode
                personId={id ? parseInt(id) : undefined}
                defaultView="categories"
              />
            </Tabs.Panel>
          </Tabs>
        </div>

        <Box style={{ width: 320, flexShrink: 0 }}>
          {id && (
            <CVPreviewSidebar
              personId={parseInt(id)}
              onClose={handleCloseSidebar}
              isEditable={false}
              key={`sidebar-${id}`}
            />
          )}
        </Box>
      </Group>
    </Container>
  );
}

// Wrap the component with EditModeProvider setting isEditMode to false
export default function CVDetailPage() {
  return (
    <EditModeProvider defaultEditMode={false}>
      <CVDetailPageContent />
    </EditModeProvider>
  );
}
